import BaseService, { type QueryParams } from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
import type { ServiceCommunicationModel } from '@/types/admin/serviceCommunication';
import {
  ADMIN_SERVICE_COMMUNICATIONS_PATH,
  applicationServiceCommunicationsPath,
  AUTHORIZATION_PROXY_BASE,
} from './constants';

/** Canonical 8-4-4-4-12 UUID row-id shape; the backend rejects anything else on `serviceIds`. */
const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Admin API for the feature/service-to-service communication matrix.
 *
 * @remarks
 * Business Rules: per-service routes nest under the owning application
 * (`admin/applications/{appId}/services/{serviceRowId}/communications`); the bulk
 * list stays flat (`admin/service-communications?serviceIds=...`) with UUID row ids.
 * Dependencies: ./constants, @/types/admin/serviceCommunication
 * Do Not: pass `serviceId` codes — communications are keyed by UUID row id.
 */
export class AdminServiceCommunicationService extends BaseService {
  constructor() {
    super(AUTHORIZATION_PROXY_BASE);
  }

  async listForService(appId: string, serviceRowId: string): Promise<ServiceCommunicationModel[]> {
    return this.get<ServiceCommunicationModel[]>({
      path: applicationServiceCommunicationsPath(appId, serviceRowId),
    });
  }

  /** Bulk fetch for one application; optionally restricted to specific caller service row ids. */
  async listAll(appId: string, serviceRowIds?: string[]): Promise<ServiceCommunicationModel[]> {
    // Forward only well-formed UUID row ids. The backend binds `serviceIds` as `List<UUID>`; a single
    // malformed/legacy id (e.g. a non-UUID value) makes `UUID.fromString` throw "UUID string too large"
    // and fails the ENTIRE request, blanking the matrix. Dropping the bad id keeps the rest working.
    const validServiceIds = (serviceRowIds ?? []).filter(id => UUID_PATTERN.test(id));
    const result = await this.get<ServiceCommunicationModel[]>({
      path: ADMIN_SERVICE_COMMUNICATIONS_PATH,
      params:
        validServiceIds.length > 0
          ? ({ appId, serviceIds: validServiceIds } as unknown as QueryParams)
          : ({ appId } as QueryParams),
    });

    if (!Array.isArray(result)) {
      console.warn('[AdminServiceCommunicationService] listAll() did not return an array, got:', typeof result);
      return [];
    }
    return result;
  }

  /** Tick a cell. The endpoint takes no body; an empty object is sent and ignored server-side. */
  async grant(appId: string, serviceRowId: string, permissionId: number): Promise<ServiceCommunicationModel | void> {
    return this.put<Record<string, never>, ServiceCommunicationModel | void>({
      path: `${applicationServiceCommunicationsPath(appId, serviceRowId)}/${permissionId}`,
      body: {},
    });
  }

  async revoke(appId: string, serviceRowId: string, permissionId: number): Promise<void> {
    try {
      await this.delete<void>({
        path: `${applicationServiceCommunicationsPath(appId, serviceRowId)}/${permissionId}`,
      });
    } catch (e: unknown) {
      // Idempotent revoke: matrix can be ahead of server, or the row was already removed.
      if (e instanceof ResponseError && e.statusCode === 404) {
        return;
      }
      throw e;
    }
  }
}
